/**
 * HTTP server for ludicrous-gateway
 * Express server setup and lifecycle management
 */

import express, { type Express } from "express";
import type { Server } from "node:http";
import type { GatewayConfig } from "../types/config.js";
import type { Provider } from "../types/providers.js";
import { DefaultRouter, createRouter } from "../routing/router.js";
import {
  DefaultContentAnalyzer,
  createAnalyzerFromRules,
} from "../analyzers/content-analyzer.js";
import { HealthTracker } from "../health/health-tracker.js";
import { createRoutes, errorHandler, requestLogger } from "./routes.js";
import { getLogger, initLogger, type Logger } from "../utils/logger.js";
import { initMetrics } from "../utils/metrics.js";

// Import provider factories
import { createAnthropicProvider } from "../providers/anthropic.js";
import { createOpenAIProvider } from "../providers/openai.js";
import { createZaiProvider } from "../providers/zai.js";
import { createOllamaProvider } from "../providers/ollama.js";
import { createKimiProvider } from "../providers/kimi.js";
import { createDeepSeekProvider } from "../providers/deepseek.js";
import { createGrokProvider } from "../providers/grok.js";
import { createMistralProvider } from "../providers/mistral.js";
import { createGeminiProvider } from "../providers/gemini.js";

/**
 * Gateway server class
 */
export class GatewayServer {
  private app: Express;
  private server: Server | null = null;
  private config: GatewayConfig;
  private providers: Map<string, Provider> = new Map();
  private router: DefaultRouter;
  private analyzer: DefaultContentAnalyzer;
  private healthTracker: HealthTracker;
  private logger: Logger;

  constructor(config: GatewayConfig) {
    this.config = config;

    // Initialize logging
    initLogger({
      level: config.logging.level,
      format: config.logging.format,
    });
    this.logger = getLogger().child({ component: "server" });

    // Initialize metrics
    initMetrics();

    // Initialize components
    this.router = createRouter(config.routing);
    this.analyzer = createAnalyzerFromRules(config.routing.rules);
    this.healthTracker = new HealthTracker(config.health);

    // Initialize providers
    this.initializeProviders();

    // Create Express app
    this.app = this.createApp();
  }

  /**
   * Initialize provider instances
   */
  private initializeProviders(): void {
    for (const providerConfig of this.config.providers) {
      if (!providerConfig.enabled) {
        this.logger.info(`Provider ${providerConfig.name} is disabled`);
        continue;
      }

      let provider: Provider;

      switch (providerConfig.type) {
        case "anthropic":
          provider = createAnthropicProvider(providerConfig);
          break;
        case "openai":
          provider = createOpenAIProvider(providerConfig);
          break;
        case "zai":
          provider = createZaiProvider(providerConfig);
          break;
        case "ollama":
          provider = createOllamaProvider(providerConfig);
          break;
        case "kimi":
          provider = createKimiProvider(providerConfig);
          break;
        case "deepseek":
          provider = createDeepSeekProvider(providerConfig);
          break;
        case "grok":
          provider = createGrokProvider(providerConfig);
          break;
        case "mistral":
          provider = createMistralProvider(providerConfig);
          break;
        case "gemini":
          provider = createGeminiProvider(providerConfig);
          break;
        default:
          this.logger.warn(`Unknown provider type: ${providerConfig.type}`);
          continue;
      }

      this.providers.set(providerConfig.name, provider);
      this.healthTracker.registerProvider(provider);

      this.logger.info(`Initialized provider: ${providerConfig.name}`, {
        type: providerConfig.type,
        baseUrl: providerConfig.base_url,
      });
    }
  }

  /**
   * Create and configure Express app
   */
  private createApp(): Express {
    const app = express();

    // Middleware
    app.use(express.json({ limit: "50mb" }));
    app.use(requestLogger);

    // Routes
    const routes = createRoutes({
      config: this.config,
      providers: this.providers,
      router: this.router,
      analyzer: this.analyzer,
      healthTracker: this.healthTracker,
    });

    app.use(routes);

    // Error handler
    app.use(errorHandler);

    return app;
  }

  /**
   * Start the server
   */
  async start(): Promise<void> {
    const [host, portStr] = this.config.server.listen.split(":");
    const port = parseInt(portStr ?? "8787", 10);

    return new Promise((resolve, reject) => {
      try {
        this.server = this.app.listen(port, host ?? "127.0.0.1", () => {
          this.logger.info(`Server listening on ${this.config.server.listen}`);

          // Start health checks
          this.healthTracker.startHealthChecks();

          resolve();
        });

        this.server.on("error", (err) => {
          this.logger.error("Server error", { error: err.message });
          reject(err);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Stop the server
   */
  async stop(): Promise<void> {
    return new Promise((resolve) => {
      // Stop health checks
      this.healthTracker.stopHealthChecks();

      if (this.server) {
        this.server.close(() => {
          this.logger.info("Server stopped");
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  /**
   * Get the Express app (for testing)
   */
  getApp(): Express {
    return this.app;
  }

  /**
   * Get provider by name
   */
  getProvider(name: string): Provider | undefined {
    return this.providers.get(name);
  }

  /**
   * Get all providers
   */
  getProviders(): Map<string, Provider> {
    return new Map(this.providers);
  }

  /**
   * Get router
   */
  getRouter(): DefaultRouter {
    return this.router;
  }

  /**
   * Get health tracker
   */
  getHealthTracker(): HealthTracker {
    return this.healthTracker;
  }

  /**
   * Reload configuration
   */
  reloadConfig(config: GatewayConfig): void {
    this.config = config;

    // Update router rules
    this.router.updateConfig({
      defaultProvider: config.routing.default_provider,
      defaultModel: config.routing.default_model,
      rules: config.routing.rules,
    });

    // Update analyzer
    this.analyzer = createAnalyzerFromRules(config.routing.rules);

    this.logger.info("Configuration reloaded");
  }
}

/**
 * Create and start a gateway server
 */
export async function createServer(config: GatewayConfig): Promise<GatewayServer> {
  const server = new GatewayServer(config);
  await server.start();
  return server;
}
